/**
 * Background-service banner. Two jobs, both about keeping the crew informed when
 * the app is relying on (or has lost) the background-location service:
 *
 *   1. A one-time guidance prompt the first time the service comes up, telling the
 *      user to allow "Allow all the time" location and turn off battery
 *      optimisation, without which Android can still throttle the watch. Dismissed
 *      with "Got it", which marks it shown so it never nags again.
 *
 *   2. A clear amber warning whenever we've had to fall back to foreground-only
 *      (a denied background-location or notification permission), so nobody
 *      assumes a screen-off alarm will sound when it won't.
 *
 * Kept out of the way and self-contained (inline styles), below the red alarm
 * banner, and it never blocks the chart.
 *
 * NOTE the pointerEvents on both banners. They sit inside .acp-shell, which is
 * pointer-events:none so the chart stays draggable through the HUD, and a child
 * that wants to be tapped has to opt back in. Without it the banner rendered
 * perfectly and its button did nothing at all, which is fb-msc9xbe8-0: "tapping
 * okay on the background watch popup won't get rid of it". A dismissable thing
 * that will not dismiss is worse than one with no button, because the user
 * keeps trying.
 */
import { useBackgroundServiceStore } from '@/store/backgroundService';
import { openAlarmSettings, requestUnrestrictedBattery } from '@/safety/anchorNative';

export function BackgroundServiceBanner() {
  const serviceRunning = useBackgroundServiceStore((s) => s.serviceRunning);
  const batteryGuidanceShown = useBackgroundServiceStore((s) => s.batteryGuidanceShown);
  const markGuidance = useBackgroundServiceStore((s) => s.markBatteryGuidanceShown);
  const degraded = useBackgroundServiceStore((s) => s.degraded);
  const degradedReason = useBackgroundServiceStore((s) => s.degradedReason);
  const degradedTitle = useBackgroundServiceStore((s) => s.degradedTitle);
  const degradedAction = useBackgroundServiceStore((s) => s.degradedAction);
  const dismissDegraded = useBackgroundServiceStore((s) => s.dismissDegraded);

  // The degrade warning takes priority: if we've fallen back, that's the thing to
  // say, not the setup guidance.
  if (degraded) {
    return (
      <div
        role="alert"
        onClick={() => dismissDegraded()}
        style={{
          position: 'absolute',
          top: 96,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'min(92vw, 460px)',
          background: 'rgba(180, 83, 9, 0.94)',
          color: '#fff',
          padding: '10px 14px',
          borderRadius: 12,
          fontSize: 13,
          lineHeight: 1.35,
          boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
          zIndex: 60,
          cursor: 'pointer',
          pointerEvents: 'auto',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 2 }}>{degradedTitle ?? 'Foreground-only'}</strong>
        {degradedReason ?? 'Background alarms and track only work with the app open.'}
        {degradedAction != null && (
          <button
            onClick={(e) => {
              // The fix, one tap away. Don't let the tap fall through to the
              // banner's dismiss, or the warning vanishes as settings open and
              // never comes back this session.
              e.stopPropagation();
              if (degradedAction === 'batteryOptimisation') void requestUnrestrictedBattery();
              else void openAlarmSettings();
            }}
            style={{
              display: 'block',
              marginTop: 8,
              background: '#fff',
              color: '#92400e',
              border: 'none',
              borderRadius: 8,
              padding: '6px 14px',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            {degradedAction === 'batteryOptimisation'
              ? 'Ask Android to leave it running'
              : 'Open notification settings'}
          </button>
        )}
        <span style={{ display: 'block', marginTop: 4, opacity: 0.8, fontSize: 11 }}>Tap to dismiss</span>
      </div>
    );
  }

  if (serviceRunning && !batteryGuidanceShown) {
    return (
      <div
        role="status"
        style={{
          position: 'absolute',
          top: 96,
          left: '50%',
          transform: 'translateX(-50%)',
          maxWidth: 'min(92vw, 460px)',
          background: 'rgba(30, 58, 95, 0.95)',
          color: '#fff',
          padding: '10px 14px',
          borderRadius: 12,
          fontSize: 13,
          lineHeight: 1.35,
          boxShadow: '0 4px 18px rgba(0,0,0,0.35)',
          zIndex: 60,
          pointerEvents: 'auto',
        }}
      >
        <strong style={{ display: 'block', marginBottom: 2 }}>Background watch on</strong>
        To keep alarms and track working with the screen off, allow{' '}
        <b>&ldquo;Allow all the time&rdquo;</b> location and turn off battery optimisation for Ardent Fix.
        <button
          onClick={() => markGuidance()}
          style={{
            display: 'block',
            marginTop: 8,
            background: '#fff',
            color: '#1e3a5f',
            border: 'none',
            borderRadius: 8,
            padding: '6px 14px',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Got it
        </button>
      </div>
    );
  }

  return null;
}
